import { Link } from 'react-router'
import { categoryPlural } from '#app/utils/category-name.ts'
import { useMarketingNav } from '../_layout.tsx'

interface NavItem {
	name: string
	href: string
}

const marketingFooterNav = {
	support: [
		{ name: 'Submit a business', href: '/submit' },
		// `/claim` only works with a `?businessId=`, which the listing pages carry.
		// Site-wide chrome has no business in hand, so it points at search — the
		// step where an owner finds their listing and claims from there.
		{ name: 'Claim your listing', href: '/search' },
		{ name: 'Contact', href: '/contact' },
	] as NavItem[],
	about: [
		{ name: 'How we score', href: '/scoring-method' },
		{ name: 'All categories', href: '/categories' },
		{ name: 'All cities', href: '/cities' },
	] as NavItem[],
	legal: [
		{ name: 'Terms of service', href: '/tos' },
		{ name: 'Privacy policy', href: '/privacy' },
	] as NavItem[],
} as const

function FooterColumn({
	title,
	items,
	className,
}: {
	title: string
	items: NavItem[]
	className?: string
}) {
	if (items.length === 0) return null
	return (
		<div className={className}>
			<h3 className="text-sm/6 font-semibold">{title}</h3>
			<ul className="mt-4 space-y-2">
				{items.map((item) => (
					<li key={item.href}>
						<Link
							to={item.href}
							className="text-muted-foreground hover:text-foreground text-sm/6"
						>
							{item.name}
						</Link>
					</li>
				))}
			</ul>
		</div>
	)
}

export function MarketingFooter() {
	const nav = useMarketingNav()

	/*
	 * Footer links are derived rather than hardcoded. The previous static list
	 * pointed only at /cities/{slug} hubs, which is how the ranking and
	 * comparison pages ended up with no inbound internal links at all — built,
	 * sitemapped, and unreachable. Anything publishable is linked here.
	 */
	// Pluralized to match the page's own H1 and the query it competes for.
	// "Best Roofing Contractor in DFW" is nobody's search. Service nouns are
	// the exception and carry their own form — see `categoryPlural`.
	const rankings: NavItem[] = (nav?.rankings ?? []).slice(0, 6).map((c) => ({
		name: `Best ${categoryPlural(c)} in DFW`,
		href: `/best/${c.slug}`,
	}))

	const popular: NavItem[] = (nav?.popularPairs ?? []).slice(0, 6).map((p) => ({
		name: `${categoryPlural({ name: p.categoryName, pluralName: p.categoryPluralName })} in ${p.cityName}`,
		href: `/${p.citySlug}/${p.categorySlug}`,
	}))

	/*
	 * Both entries are gated on the same signal for the same reason: a link to
	 * a hub with nothing behind it is a dead end, and a hub with something
	 * behind it and no link is an orphan. /blog was the second case. Its posts
	 * were in sitemap.xml while the hub itself was commented out of the sitemap
	 * and linked from nowhere in the chrome, so a published post was reachable
	 * by a crawler and by no reader.
	 */
	const about: NavItem[] = [
		...((nav?.comparisonCount ?? 0) > 0
			? [{ name: 'Head-to-head comparisons', href: '/compare' }]
			: []),
		...((nav?.postCount ?? 0) > 0 ? [{ name: 'Blog', href: '/blog' }] : []),
		...marketingFooterNav.about,
	]

	return (
		<footer className="border-t">
			<div className="container mx-auto px-6 pt-16 pb-8 lg:px-8">
				<div className="xl:grid xl:grid-cols-3 xl:gap-8">
					<div className="space-y-4">
						<Link
							className="flex cursor-pointer items-center gap-2 font-serif text-xl font-bold tracking-tight"
							to="/"
						>
							<div className="bg-accent flex h-8 w-8 items-center justify-center rounded-sm font-serif text-white">
								D
							</div>
							<span>DFW Professionals</span>
						</Link>
						<p className="text-muted-foreground max-w-sm text-sm/6 text-balance">
							We score Dallas–Fort Worth professionals against a published
							rubric built from public Google reviews. Rankings are earned, not
							sold.
						</p>
						<p className="text-muted-foreground text-sm/6">
							<Link
								to="/scoring-method"
								className="hover:text-foreground underline underline-offset-4"
							>
								Read the scoring method
							</Link>
						</p>
					</div>

					<div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
						<div className="md:grid md:grid-cols-2 md:gap-8">
							<FooterColumn title="Rankings" items={rankings} />
							<FooterColumn
								title="Popular"
								items={popular}
								className={rankings.length > 0 ? 'mt-10 md:mt-0' : undefined}
							/>
						</div>

						<div className="md:grid md:grid-cols-2 md:gap-8">
							<FooterColumn title="About" items={about} />
							<div className="mt-10 md:mt-0">
								<FooterColumn
									title="For businesses"
									items={[...marketingFooterNav.support]}
								/>
								<div className="mt-10">
									<FooterColumn
										title="Legal"
										items={[...marketingFooterNav.legal]}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="mt-16 border-t pt-8">
					<p className="text-muted-foreground text-sm/6">
						&copy; {new Date().getFullYear()} DFW Professionals. All rights reserved.
					</p>
				</div>
			</div>
		</footer>
	)
}
